import type { ToastDesignTokens } from '@primeuix/themes/types/toast'

export default {
  icon: {
    size: '1.25rem',
  },
  root: {
    width: '25rem',
    borderWidth: '0 0 0 6px',
    borderRadius: '{content.border.radius}',
    transitionDuration: '{transition.duration}',
  },
  text: {
    gap: '0.5rem',
  },
  detail: {
    fontSize: '0.875rem',
    fontWeight: '500',
  },
  content: {
    gap: '0.5rem',
    padding: '{overlay.popover.padding}',
  },
  summary: {
    fontSize: '1rem',
    fontWeight: '500',
  },
  closeIcon: {
    size: '1rem',
  },
  closeButton: {
    width: '2rem',
    height: '2rem',
    focusRing: {
      style: '{focus.ring.style}',
      width: '{focus.ring.width}',
      offset: '{focus.ring.offset}',
    },
    borderRadius: '50%',
  },
  colorScheme: {
    dark: {
      info: {
        color: '{blue.500}',
        shadow: '{overlay.popover.shadow}',
        background: 'color-mix(in srgb, {blue.500}, transparent 84%)',
        borderColor: 'color-mix(in srgb, {blue.700}, transparent 64%)',
        closeButton: {
          focusRing: {
            color: '{focus.ring.color}',
            shadow:
              '0 0 0 0.2rem color-mix(in srgb, {blue.500}, transparent 80%)',
          },
          hoverBackground: 'rgba(255, 255, 255, 0.05)',
        },
        detailColor: '{surface.0}',
      },
      root: {
        blur: '10px',
      },
      warn: {
        color: '{yellow.500}',
        shadow: '{overlay.popover.shadow}',
        background: 'color-mix(in srgb, {yellow.500}, transparent 84%)',
        borderColor: 'color-mix(in srgb, {yellow.700}, transparent 64%)',
        closeButton: {
          focusRing: {
            color: '{focus.ring.color}',
            shadow:
              '0 0 0 0.2rem color-mix(in srgb, {yellow.500}, transparent 80%)',
          },
          hoverBackground: 'rgba(255, 255, 255, 0.05)',
        },
        detailColor: '{surface.0}',
      },
      error: {
        color: '{red.500}',
        shadow: '{overlay.popover.shadow}',
        background: 'color-mix(in srgb, {red.500}, transparent 84%)',
        borderColor: 'color-mix(in srgb, {red.700}, transparent 64%)',
        closeButton: {
          focusRing: {
            color: '{focus.ring.color}',
            shadow:
              '0 0 0 0.2rem color-mix(in srgb, {red.500}, transparent 80%)',
          },
          hoverBackground: 'rgba(255, 255, 255, 0.05)',
        },
        detailColor: '{surface.0}',
      },
      success: {
        color: '{green.500}',
        shadow: '{overlay.popover.shadow}',
        background: 'color-mix(in srgb, {green.500}, transparent 84%)',
        borderColor: 'color-mix(in srgb, {green.700}, transparent 64%)',
        closeButton: {
          focusRing: {
            color: '{focus.ring.color}',
            shadow:
              '0 0 0 0.2rem color-mix(in srgb, {green.500}, transparent 80%)',
          },
          hoverBackground: 'rgba(255, 255, 255, 0.05)',
        },
        detailColor: '{surface.0}',
      },
      contrast: {
        color: '{surface.950}',
        shadow: '{overlay.popover.shadow}',
        background: '{surface.0}',
        borderColor: '{surface.100}',
        closeButton: {
          focusRing: {
            color: '{focus.ring.color}',
            shadow:
              '0 0 0 0.2rem color-mix(in srgb, {surface.950}, transparent 80%)',
          },
          hoverBackground: '{surface.100}',
        },
        detailColor: '{surface.950}',
      },
      secondary: {
        color: '{surface.300}',
        shadow: '{overlay.popover.shadow}',
        background: '{surface.800}',
        borderColor: '{surface.700}',
        closeButton: {
          focusRing: {
            color: '{focus.ring.color}',
            shadow:
              '0 0 0 0.2rem color-mix(in srgb, {surface.300}, transparent 80%)',
          },
          hoverBackground: '{surface.700}',
        },
        detailColor: '{surface.0}',
      },
    },
    light: {
      info: {
        color: '{surface.0}',
        shadow: '{overlay.popover.shadow}',
        background: '{nash.midnightBlue.700}',
        borderColor: '{nash.midnightBlue.700}',
        closeButton: {
          focusRing: {
            color: '{nash.midnightBlue.700}',
            shadow: '0 0 0 0.2rem {blue.200}',
          },
          hoverBackground: '{nash.midnightBlue.700}',
        },
        detailColor: '{surface.0}',
      },
      root: {
        blur: '1.5px',
      },
      warn: {
        color: '{surface.800}',
        shadow: '{overlay.popover.shadow}',
        background: '{nash.tigerlily.800}',
        borderColor: '{nash.tigerlily.800}',
        closeButton: {
          focusRing: {
            color: '{focus.ring.color}',
            shadow: '0 0 0 0.2rem {yellow.200}',
          },
          hoverBackground: '{nash.mustard.650}',
        },
        detailColor: '{surface.700}',
      },
      error: {
        color: '{surface.0}',
        shadow: '{overlay.popover.shadow}',
        background: '{nash.tigerlily.800}',
        borderColor: '{nash.tigerlily.800}',
        closeButton: {
          focusRing: {
            color: '{focus.ring.color}',
            shadow: '0 0 0 0.2rem {red.200}',
          },
          hoverBackground: '{red.100}',
        },
        detailColor: '{nash.tigerlily.800}',
      },
      success: {
        color: '{green.600}',
        shadow: '{overlay.popover.shadow}',
        background: 'color-mix(in srgb, {green.50}, transparent 5%)',
        borderColor: '{green.500}',
        closeButton: {
          focusRing: {
            color: '{focus.ring.color}',
            shadow: '0 0 0 0.2rem {green.200}',
          },
          hoverBackground: '{green.100}',
        },
        detailColor: '{surface.700}',
      },
      contrast: {
        color: '{surface.50}',
        shadow: '{overlay.popover.shadow}',
        background: '{surface.900}',
        borderColor: '{primary.color}',
        closeButton: {
          focusRing: {
            color: '{focus.ring.color}',
            shadow: '0 0 0 0.2rem {surface.400}',
          },
          hoverBackground: '{surface.800}',
        },
        detailColor: '{surface.0}',
      },
      secondary: {
        color: '{surface.600}',
        shadow: '{overlay.popover.shadow}',
        background: '{surface.100}',
        borderColor: '{surface.500}',
        closeButton: {
          focusRing: {
            color: '{focus.ring.color}',
            shadow: '0 0 0 0.2rem {surface.200}',
          },
          hoverBackground: '{surface.200}',
        },
        detailColor: '{surface.700}',
      },
    },
  },
} satisfies ToastDesignTokens
